/**
 * Transform functions: EAV BlRecordResponseDTO[] → legacy component shapes.
 *
 * Each transform takes an array of EAV records (from GET /api/investments/{id}/financials)
 * and returns the exact shape the child component already expects.
 *
 * The EAV record shape:
 *   { guid, blObjectId, fields: [{ fieldName, fieldDisplayName, value, ... }], ... }
 */

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/** Convert a record's fields array to a { fieldName: value } map. */
function fieldsToMap(record) {
	const map = {}
	if (record?.fields) {
		for (const f of record.fields) {
			map[f.fieldName] = f.value
		}
	}
	return map
}

// ---------------------------------------------------------------------------
// Overview  (investment_overview → CompanyOverview.vue sourceData)
// ---------------------------------------------------------------------------

// Maps EAV fieldName → { section, key } in the legacy overview object.
// CompanyOverview uses fuzzy key matching so exact case matters less for most
// fields, but the parent headingData computed expects exact section names.
const OVERVIEW_FIELD_MAP = {
	// Company Attributes
	investment_status:          { section: 'Company Attributes', key: 'Investment Status' },
	ceo:                        { section: 'Company Attributes', key: 'CEO' },
	cfo:                        { section: 'Company Attributes', key: 'CFO' },
	industry:                   { section: 'Company Attributes', key: 'Industry' },
	company_description:        { section: 'Company Attributes', key: 'Company Description (Short)'},
	company_description_long:   { section: 'Company Attributes', key: 'Company Description'},
	location_hq:                { section: 'Company Attributes', key: 'Location (headquarters)',
		alias: { section: 'Company Attributes', key: 'HQ city, state, country' } },
	deal_type:                  { section: 'Company Attributes', key: 'Deal type' },
	ownership_at_entry_pct:     { section: 'Company Attributes', key: 'Ownership at entry (%)' },
	lead_bankers:               { section: 'Company Attributes', key: 'Lead Ibanker(s) at Entry (Name of Person(s)' },
	seller_name:                { section: 'Company Attributes', key: 'Seller Name (If Financial)' },
	entry_date:                 { section: 'Company Attributes', key: 'Date of Initial Investment' },
	hq_city_state_country:       { section: 'Company Attributes', key: 'HQ city, state, country' },
	legal_form:                  { section: 'Company Attributes', key: 'Legal form of company' },
	follow_on_equity:            { section: 'Company Attributes', key: 'Follow-on equity, incl. invested capital in add-ons' },
	sourcing:                    { section: 'Company Attributes', key: 'Sourcing' },
	attribution:                 { section: 'Company Attributes', key: 'Attribution' },
	board_seats:                 { section: 'Company Attributes', key: '# of board seats at the company' },
	board_members:               { section: 'Company Attributes', key: 'Board members from the fund' },
	prior_ownership:             { section: 'Company Attributes', key: 'Prior Ownership' },
	investment_thesis:           { section: 'Company Attributes', key: 'Investment thesis' },
	gps_co_invested:             { section: 'Company Attributes', key: 'Number of GPs co-invested' },
	lps_co_invested:             { section: 'Company Attributes', key: 'Number of LPs co-invested' },
	lead_co_lead:                { section: 'Company Attributes', key: 'Lead/Co-lead' },
	deal_team:                   { section: 'Company Attributes', key: 'Deal Team' },
	covenant_status:             { section: 'Company Attributes', key: 'Covenant status' },
	covenant_status_comment:     { section: 'Company Attributes', key: 'Comment on covenant status' },
	security_type:               { section: 'Company Attributes', key: 'Security type' },
	seller_type:                 { section: 'Company Attributes', key: 'Seller type' },
	mgmt_transition_flag:        { section: 'Company Attributes', key: 'Mgmt team transition flag (Y/N)' },
	source_investment_bank:      { section: 'Company Attributes', key: 'Source / investment bank' },
	qoe_tax_firm:                { section: 'Company Attributes', key: 'Qoe / tax firm used for dd' },
	current_auditors:            { section: 'Company Attributes', key: 'current auditors' },
	legal_advisor:               { section: 'Company Attributes', key: 'legal advisor used' },
	seller_financing_flag:       { section: 'Company Attributes', key: 'Seller financing flag (Y / N)' },
	rwi_insurance_flag:          { section: 'Company Attributes', key: 'RWI insurance flag (Y / N)' },
	rwi_provider:                { section: 'Company Attributes', key: 'rwi provider used' },
	indemnification_cap:         { section: 'Company Attributes', key: 'Indemnification cap' },
	general_survival_period:     { section: 'Company Attributes', key: 'general survival period' },
	basket:                      { section: 'Company Attributes', key: 'Basket' },
	escrow_holdback:             { section: 'Company Attributes', key: 'Escrow/ Holdback' },
	escrow_holdback_period:      { section: 'Company Attributes', key: 'Escrow/Holdback Period' },

	// Financial Information (At Entry)
	entry_as_of:                { section: 'Financial Information (At Entry)', key: 'As of :' },
	entry_ltm_revenue:          { section: 'Financial Information (At Entry)', key: 'LTM Revenue' },
	entry_ltm_ebitda:           { section: 'Financial Information (At Entry)', key: 'LTM EBITDA' },
	entry_enterprise_value:     { section: 'Financial Information (At Entry)', key: 'Enterprise Value' },
	entry_net_debt:             { section: 'Financial Information (At Entry)', key: 'Net Debt' },
	entry_purchase_multiple:    { section: 'Financial Information (At Entry)', key: 'Purchase Multiple' },
	entry_senior_debt:          { section: 'Financial Information (At Entry)', key: 'Senior Debt' },
	entry_sub_debt:             { section: 'Financial Information (At Entry)', key: 'Subordinated Debt' },
	entry_equity_value:         { section: 'Financial Information (At Entry)', key: 'Equity Value' },
	entry_bertram_equity:        { section: 'Financial Information (At Entry)', key: 'Bertram Funds Equity Invested' },
	entry_bertram_sub_debt:      { section: 'Financial Information (At Entry)', key: 'Bertram Funds Sub Debt Invested' },
	entry_unitranche_debt:       { section: 'Financial Information (At Entry)', key: 'Unitranche Debt' },
	entry_total_leverage:        { section: 'Financial Information (At Entry)', key: 'Total Leverage' },
	entry_rollover_equity_pct:   { section: 'Financial Information (At Entry)', key: 'Rollover Equity %' },
	entry_rollover_equity_amt:   { section: 'Financial Information (At Entry)', key: 'Rollover Equity $' },
	entry_co_invest_equity:      { section: 'Financial Information (At Entry)', key: 'Co Invest Equity $' },
	entry_cash:                  { section: 'Financial Information (At Entry)', key: 'Cash' },
	entry_buyers_transaction_expenses: { section: 'Financial Information (At Entry)', key: "Buyer's Transaction Expenses" },

	// Value Creation
	value_creation_debt_paydown_pct:         { section: 'Value Creation', key: 'Debt Paydown / Other' },
	value_creation_ebitda_growth_pct:        { section: 'Value Creation', key: 'EBITDA Growth' },
	value_creation_multiple_expansion_pct:   { section: 'Value Creation', key: 'Multiple Expansion' },
	leverage_posture:                        { section: 'Value Creation', key: 'Leverage Posture' },

	// Text blocks
	investment_rationale:       { section: 'Investment Rationale', key: '_text' },
	value_add_proposition:      { section: 'Value-Add Proposition at Entry', key: '_text' },

	// JSON blob sections (handled specially in transformOverview)
	fund_allocation:            { section: '_json', target: 'Bertram Fund Allocation' },
	company_ownership:          { section: '_json', target: 'Underlying Portfolio Company Ownership' },
	exit_financials:            { section: '_json', target: 'Financial Information (At Exit)' },
	val_information:            { section: '_json', target: 'Valuation Information' },
	value_drivers:              { section: '_json', target: 'Prior Fund Value Drivers' },
}

/**
 * Transform a single investment_overview EAV record into the section-keyed
 * object that CompanyOverview.vue expects.
 */
export function transformOverview(records) {
	if (!records || records.length === 0) return {}

	const record = records[0]
	const fields = fieldsToMap(record)

	// Seed all sections as empty objects
	const result = {
		'Company Attributes': {},
		'Financial Information (At Entry)': {},
		'Financial Information (At Exit)': {},
		'Valuation Information': {},
		'Value Creation': {},
		'Prior Fund Value Drivers': {},
		'Bertram Fund Allocation': {},
		'Underlying Portfolio Company Ownership': {},
		'Investment Rationale': null,
		'Value-Add Proposition at Entry': null,
	}

	// Extract the UTC date portion from the processing timestamp for display
	result.asOf = fields.processed_at ? fields.processed_at.substring(0, 10) : null

	for (const [fieldName, value] of Object.entries(fields)) {
		const mapping = OVERVIEW_FIELD_MAP[fieldName]
		if (!mapping) continue

		if (mapping.section === '_json') {
			// JSON blob sections: parse and spread into target section
			try {
				const parsed = typeof value === 'string' ? JSON.parse(value) : value
				if (parsed && typeof parsed === 'object') {
					result[mapping.target] = parsed
				}
			} catch (_) { /* leave as empty object */ }
		} else if (mapping.key === '_text') {
			// Text blocks: split by newline into .text array
			const text = value ? String(value).split('\n') : ['']
			result[mapping.section] = { text }
		} else {
			result[mapping.section][mapping.key] = value
			// Emit alias key so both parent (direct access) and child (fuzzy) work
			if (mapping.alias) {
				result[mapping.alias.section][mapping.alias.key] = value
			}
		}
	}

	// Compute Value Creation total as sum of the three percentage components
	const vc = result['Value Creation']
	const debtPct = Number(vc['Debt Paydown / Other']) || 0
	const ebitdaPct = Number(vc['EBITDA Growth']) || 0
	const multPct = Number(vc['Multiple Expansion']) || 0
	const total = debtPct + ebitdaPct + multPct
	if (total !== 0) {
		vc['Total'] = total
	}

	return result
}


// ---------------------------------------------------------------------------
// Financials  (investment_financials → FinancialsActuals.vue sourceData)
// ---------------------------------------------------------------------------

// Maps EAV fieldName → the exact valueField string FinancialsActuals expects.
const FINANCIALS_FIELD_MAP = {
	revenue:                         'Revenue',
	revenue_adjusted:                'Revenue (Adjusted)',
	gross_profit:                    'Gross Profit',
	gross_margin:                    'Gross Margin',
	ebitda:                          'EBITDA',
	ebitda_adjusted:                 'EBITDA (Adjusted)',
	ebitda_margin:                   'EBITDA Margin',
	capex:                           'Capex',
	enterprise_value:                'Enterprise Value',
	total_leverage:                  'Total Leverage',
	total_leverage_ratio:            'Total leverage ratio',
	net_debt:                        'Net Debt',
	senior_debt:                     'Senior Debt',
	unitranche:                      'Unitranche',
	sub_debt:                        'Subordinated Debt',
	cash:                            'Cash',
	total_fmv:                       'Total FMV (Debt + Equity)',
	bertram_equity_fmv:              'Bertram Equity FMV',
	bertram_equity_fmv_mgmt_fee_offsets: 'Bertram Equity FMV + Mgmt Fee Offsets',
	bertram_realized_equity_value:   'Bertram Realized Equity Value',
	bertram_unrealized_fmv_equity_value: 'Bertram Unrealized FMV Equity Value',
	total_equity_fmv:                'Total Equity FMV',
	total_realized_equity_value:     'Total Realized Equity Value',
	total_unrealized_fmv_equity_value: 'Total Unrealized FMV Equity Value',
	debt_fmv:                        'Debt FMV',
	tev_ebitda_multiple:             'TEV / EBITDA Multiple',
	bertram_funds_equity_invested:   'Bertram Funds Equity Invested',
	bertram_funds_sub_debt_invested: 'Bertram Funds Sub Debt Invested',
	total_equity_invested:           'Total Equity Invested',
	bertram_equity_debt_gain_loss:   'Bertram Equity & Debt Gain / (Loss)',
	cost_basis_ownership_multiple:   'Cost Basis (Ownership Multiple)',
	transaction_fees_paid:           'Transaction & Advisory Fees Paid to Mgmt Co not used to Offset Mgmt Fees',
	transaction_fees_applied:        'Transaction & Advisory Fees Applied to Mgmt Fee Offsets',
	bertram_moic:                    'Bertram Gross MOIC (Equity, Exc. Fees)',
	gross_moic:                      'Gross MOIC',
	net_moic:                        'Net MOIC',
	gross_irr:                       'Gross IRR',
	net_irr:                         'Net IRR',
	ltm_revenue:                     'LTM Revenue',
	ltm_gross_profit:                'LTM Gross Profit',
	ltm_gross_margin:                'LTM Gross Margin',
	ltm_ebitda:                      'LTM EBITDA',
	ltm_ebitda_margin:               'LTM EBITDA Margin',
	ltm_capex:                       'LTM Capex',
	ltm_free_cash_flow:              'LTM Free Cash Flow',
	ltm_interest_charges:            'LTM Interest Charges',
	interest_coverage:               'EBITDA/Interest Expense',
	ebitda_capex_interest_expense:   'EBITDA-CAPEX/Interest Expense',
	net_debt_ebitda:                 'Net Debt/EBITDA',
	notes:                           'Notes',
}

/**
 * Transform investment_financials EAV records → array of { date, data } objects.
 * FinancialsActuals pivots on `date` as the column key.
 */
export function transformFinancials(records) {
	if (!records || records.length === 0) return []

	return records
		.map(record => {
			const fields = fieldsToMap(record)
			const data = {}

			for (const [fieldName, value] of Object.entries(fields)) {
				if (fieldName === 'period_date') continue
				const displayKey = FINANCIALS_FIELD_MAP[fieldName]
				if (displayKey && value != null) {
					data[displayKey] = value
				}
			}

			return {
				date: fields.period_date || '',
				data
			}
		})
		.filter(item => Object.keys(item.data).length > 0)
}


// ---------------------------------------------------------------------------
// Co-Invest  (investment_coinvest → CoInvestLenders.vue sourceData)
// ---------------------------------------------------------------------------

// Section values from the EAV pickList → legacy group keys
const COINVEST_SECTION_MAP = {
	'Co-Invest Equity': 'coInvest',
	'LP Mezz Debt':     'mezzDebt',
	'LP Senior Debt':   'seniorDebt',
}

/**
 * Transform investment_coinvest EAV records → the array that CoInvestLenders expects.
 * Output: [{ data: { coInvest: [...], mezzDebt: [...], seniorDebt: [...], summary: [] } }]
 */
export function transformCoinvest(records) {
	if (!records || records.length === 0) return []

	const groups = { coInvest: [], mezzDebt: [], seniorDebt: [], summary: [] }

	for (const record of records) {
		const fields = fieldsToMap(record)

		if (fields.section === 'Co-Invest Summary') {
			groups.summary.push({ 'BCAP Equity': fields.bcap_equity ?? null })
			continue
		}

		const section = COINVEST_SECTION_MAP[fields.section]
		if (!section) continue

		if (section === 'coInvest') {
			groups.coInvest.push({
				coInvestor: fields.holder_name || '',
				acq: fields.acq_amount ?? null,
				'Ownership % at ACQ': fields.acq_ownership_pct ?? null,
				current: fields.current_amount ?? null,
				'Current Ownership %': fields.current_ownership_pct ?? null,
			})
		} else {
			// mezzDebt or seniorDebt
			groups[section].push({
				debtHolder: fields.holder_name || '',
				acq: fields.acq_amount ?? null,
				current: fields.current_amount ?? null,
			})
		}
	}

	return [{ data: groups }]
}


// ---------------------------------------------------------------------------
// Lenders  (investment_lenders → CoInvestLenders.vue lenders prop)
// ---------------------------------------------------------------------------

/**
 * Transform investment_lenders EAV records → array of { data: { Lender, ... } }.
 */
export function transformLenders(records) {
	if (!records || records.length === 0) return []

	return records.map(record => {
		const fields = fieldsToMap(record)
		return {
			data: {
				'Lender': fields.lender_name || '',
				'Lender Type': fields.lender_type || '',
				'Dollars Invested': fields.dollars_invested ?? null,
				'Pricing': fields.pricing || '',
				'Alloc.': fields.allocation_pct ?? null,
			}
		}
	})
}


// ---------------------------------------------------------------------------
// Acquisitions  (investment_acquisitions → AddOnAcquisitions.vue sourceData)
// ---------------------------------------------------------------------------

/**
 * Transform investment_acquisitions EAV records → array that AddOnAcquisitions expects.
 * The component does `acquisition.data || acquisition` so we can emit either shape.
 */
export function transformAcquisitions(records) {
	if (!records || records.length === 0) return []

	return records.map(record => {
		const fields = fieldsToMap(record)
		return {
			'Add-on Company Name': fields.addon_company_name || '',
			'Entry Date': fields.entry_date || '',
			'Enterprise Value at Entry': fields.enterprise_value_at_entry ?? null,
			'Equity at Entry': fields.equity_at_entry ?? null,
			'Net Debt at Entry': fields.net_debt_at_entry ?? null,
			'Revenue at Entry': fields.revenue_at_entry ?? null,
			'EBITDA at Entry': fields.ebitda_at_entry ?? null,
			'EBITDA Purch. Mult. At Entry': fields.ebitda_purchase_multiple ?? null,
			'BCAP Equity Invested': fields.bcap_equity_invested ?? null,
			'BCAP Sub Debt Invested': fields.bcap_sub_debt_invested ?? null,
		}
	})
}


// ---------------------------------------------------------------------------
// ESG Form  (investment_esg_form → EsgForm.vue sourceData)
// ---------------------------------------------------------------------------

/**
 * Transform investment_esg_form EAV records → { data: [{ label, value, displayOrder }] }.
 */
export function transformEsgForm(records) {
	if (!records || records.length === 0) return {}

	const data = records.map(record => {
		const fields = fieldsToMap(record)
		return {
			label: fields.label || '',
			value: fields.value ?? null,
			displayOrder: fields.display_order ?? 0,
		}
	})

	return { data }
}


// ---------------------------------------------------------------------------
// ESG Scorecard  (investment_esg_scorecard → EsgScorecard.vue sourceData)
// ---------------------------------------------------------------------------

/**
 * Transform investment_esg_scorecard EAV records →
 * { data: { asOf, entries: [{ dimension, subEntries: [{ criteria, initial, current }] }] } }
 */
export function transformEsgScorecard(records) {
	if (!records || records.length === 0) return {}

	// Group records by dimension, preserving display_order
	const dimensionMap = {}

	for (const record of records) {
		const fields = fieldsToMap(record)
		const dimension = fields.dimension || 'Unknown'

		if (!dimensionMap[dimension]) {
			dimensionMap[dimension] = []
		}

		dimensionMap[dimension].push({
			order: fields.display_order ?? 0,
			criteria: fields.criteria ? String(fields.criteria).split('\n') : [],
			initial: {
				score: fields.initial_score ?? null,
				status: fields.initial_status || '',
				opportunities: fields.initial_opportunities || '',
			},
			current: {
				score: fields.current_score ?? null,
				status: fields.current_status || '',
				opportunities: fields.current_opportunities || '',
			},
		})
	}

	// Build entries array sorted by first appearance order
	const entries = []
	for (const [dimension, subEntries] of Object.entries(dimensionMap)) {
		subEntries.sort((a, b) => a.order - b.order)
		entries.push({
			dimension,
			subEntries: subEntries.map(({ criteria, initial, current }) => ({
				criteria,
				initial,
				current,
			})),
		})
	}

	// Use the most recent record's lastUpdated as asOf
	const latestRecord = records.reduce((latest, r) =>
		(!latest || (r.lastUpdated && r.lastUpdated > (latest.lastUpdated || ''))) ? r : latest
	, null)

	return {
		data: {
			asOf: latestRecord?.lastUpdated || null,
			entries,
		}
	}
}
